import React, { useState, useRef, useEffect } from 'react';
import ProductCard from './ProductCard';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProductCarousel = ({ title, products, showViewAll = true, viewAllLink = "/product-catalog-category-browse" }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    checkScrollButtons();
  }, [products]);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280; // Approximate card width + gap
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-heading-semibold text-xl md:text-2xl text-text-primary">
          {title}
        </h2>
        <div className="flex items-center space-x-2">
          {showViewAll && (
            <a
              href={viewAllLink}
              className="flex items-center space-x-2 text-primary hover:text-secondary transition-smooth mr-4"
            >
              <span className="font-body-semibold text-sm">View All</span>
              <Icon name="ArrowRight" size={16} />
            </a>
          )}
          
          {/* Navigation Buttons */}
          <div className="hidden md:flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="w-10 h-10 p-0"
              iconName="ChevronLeft"
              iconSize={16}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="w-10 h-10 p-0"
              iconName="ChevronRight"
              iconSize={16}
            />
          </div>
        </div>
      </div>

      {/* Products Container */}
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="flex space-x-4 overflow-x-auto scrollbar-hide pb-2"
          onScroll={checkScrollButtons}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <div key={product.id} className="flex-shrink-0 w-64 sm:w-72">
              <ProductCard product={product} />
            </div>
          ))}
        </div>

        {/* Mobile Navigation Indicators */}
        <div className="md:hidden flex justify-center mt-4 space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="w-8 h-8 p-0"
            iconName="ChevronLeft"
            iconSize={16}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="w-8 h-8 p-0"
            iconName="ChevronRight"
            iconSize={16}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCarousel;